import { useEffect, useRef, useState } from "react";
import PageHero from "@/components/PageHero";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { sendContact } from "@/lib/api";
import { toast } from "sonner";
import { Clock, Shield, MessageSquare } from "lucide-react";

const TOPICS = [
    "General Question",
    "Interview Request",
    "Billing",
    "Collaboration",
    "Praises",
    "Constructive Feedback",
];

const SIDEBAR = [
    {
        icon: MessageSquare,
        title: "One inbox, six lanes",
        copy: "Pick the right topic below — your message is routed straight to the editor who handles it. No more guessing which address to use.",
    },
    {
        icon: Clock,
        title: "Reply within 2 business days",
        copy: "Interview Requests and Billing items are bumped to the front of the queue. We never publish your message.",
    },
    {
        icon: Shield,
        title: "Spam-filtered, not surveilled",
        copy: "Honeypot + content scanning blocks bots. We don't run ad trackers or sell your data. Only the team sees your note.",
    },
];

export default function ContactPage() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        topic: TOPICS[0],
        subject: "",
        message: "",
        website_url: "", // honeypot
    });
    const renderedAtRef = useRef(Date.now());
    const [done, setDone] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        renderedAtRef.current = Date.now();
    }, []);

    const set = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            await sendContact({
                ...form,
                rendered_at: renderedAtRef.current,
            });
            toast.success("Message received.", {
                description: "We reply within 2 business days.",
            });
            setForm({
                name: "",
                email: "",
                topic: TOPICS[0],
                subject: "",
                message: "",
                website_url: "",
            });
            setDone(true);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (err?.response?.status === 429) {
                toast.error(
                    detail ||
                        "Too many messages from this IP — try again later.",
                );
            } else {
                toast.error(detail || "Failed to send. Please try again.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <PageHero
                eyebrow="Get in touch"
                title="Contact BENNE"
                lead="One form — pick your topic and we'll route it to the right desk. Press tips, partnership ideas, billing, interviews, praises, and honest feedback all welcome."
                breadcrumbs={[
                    { label: "Home", to: "/" },
                    { label: "Contact" },
                ]}
            />

            <div className="px-4 md:px-8 lg:px-12 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
                {done ? (
                    <div
                        className="lg:col-span-7 bg-[#1B1B1B] text-white p-8 md:p-10"
                        data-testid="contact-success"
                    >
                        <p className="font-poppins font-black text-3xl text-[#F5C53D]">
                            Got it.
                        </p>
                        <p className="font-sofia text-white/85 mt-3">
                            Your note is on the editor's desk. Expect a reply
                            within 2 business days. You'll hear back at the
                            email you provided.
                        </p>
                        <Button
                            onClick={() => setDone(false)}
                            variant="ghost"
                            className="mt-6 text-[#F5C53D] hover:bg-transparent hover:text-white"
                            data-testid="contact-send-another"
                        >
                            Send another message →
                        </Button>
                    </div>
                ) : (
                    <form
                        onSubmit={submit}
                        className="lg:col-span-7 space-y-5 bg-white border border-black/10 p-6 md:p-8"
                        data-testid="contact-form"
                        noValidate
                    >
                        <h2 className="font-poppins font-bold text-2xl text-[#1B1B1B]">
                            Send us a note
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="c-name">Name *</Label>
                                <Input
                                    id="c-name"
                                    value={form.name}
                                    onChange={set("name")}
                                    required
                                    data-testid="contact-name"
                                />
                            </div>
                            <div>
                                <Label htmlFor="c-email">Email *</Label>
                                <Input
                                    id="c-email"
                                    type="email"
                                    value={form.email}
                                    onChange={set("email")}
                                    required
                                    data-testid="contact-email"
                                />
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="c-topic">This is regarding *</Label>
                            <Select
                                value={form.topic}
                                onValueChange={set("topic")}
                            >
                                <SelectTrigger
                                    id="c-topic"
                                    data-testid="contact-topic"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {TOPICS.map((t) => (
                                        <SelectItem
                                            key={t}
                                            value={t}
                                            data-testid={`contact-topic-${t.toLowerCase().replace(/\s+/g, "-")}`}
                                        >
                                            {t}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label htmlFor="c-subject">Subject *</Label>
                            <Input
                                id="c-subject"
                                value={form.subject}
                                onChange={set("subject")}
                                required
                                data-testid="contact-subject"
                            />
                        </div>

                        <div>
                            <Label htmlFor="c-message">Message *</Label>
                            <Textarea
                                id="c-message"
                                rows={6}
                                value={form.message}
                                onChange={set("message")}
                                required
                                minLength={20}
                                data-testid="contact-message"
                            />
                            <p className="font-sofia text-xs text-[#1B1B1B]/50 mt-1">
                                Minimum 20 characters. We read everything.
                            </p>
                        </div>

                        {/* Honeypot — hidden field, real users never fill it */}
                        <div
                            aria-hidden="true"
                            tabIndex={-1}
                            style={{
                                position: "absolute",
                                left: "-9999px",
                                top: "-9999px",
                                width: 1,
                                height: 1,
                                opacity: 0,
                                pointerEvents: "none",
                            }}
                        >
                            <label htmlFor="c-website">
                                Website (leave blank)
                            </label>
                            <input
                                id="c-website"
                                type="text"
                                autoComplete="off"
                                tabIndex={-1}
                                value={form.website_url}
                                onChange={set("website_url")}
                                name="website_url"
                            />
                        </div>

                        <Button
                            type="submit"
                            disabled={submitting}
                            className="bg-[#E63B26] hover:bg-[#1B1B1B] text-white font-poppins tracking-[0.18em] uppercase rounded-none h-12 px-6 disabled:opacity-60"
                            data-testid="contact-submit"
                        >
                            {submitting ? "Sending…" : "Send message →"}
                        </Button>
                    </form>
                )}

                <aside className="lg:col-span-5 space-y-4">
                    {SIDEBAR.map((c) => (
                        <div
                            key={c.title}
                            className="border border-black/10 p-5 bg-white hover:border-[#E63B26] transition"
                            data-testid={`contact-info-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            <div className="flex items-start gap-3">
                                <div className="bg-[#1B1B1B] text-[#F5C53D] p-2.5">
                                    <c.icon className="h-5 w-5" />
                                </div>
                                <div>
                                    <p className="font-poppins font-bold text-base text-[#1B1B1B]">
                                        {c.title}
                                    </p>
                                    <p className="font-sofia text-sm text-[#1B1B1B]/70 mt-1">
                                        {c.copy}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </aside>
            </div>
        </div>
    );
}
